import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { CalendarRange, Globe, MapPin, Info } from "lucide-react";

/**
 * AdminSeasonMatrix — Editor de preços por temporada usado nas modals do Admin
 * (Tours, Rotas Fixas, Categorias de Veículos).
 *
 * Props:
 *   seasons: Array<Season>
 *   categories: Array<{key,label}> (para tours/rotas)
 *   zoneKey: string (filtra seasons aplicáveis)
 *   seasonPrices: {[season_id]: {[cat_key or field]: price}}
 *   onChange: (newSeasonPrices) => void
 *   basePrices: {[cat_key]: price} (readonly reference)
 *   priceFields?: string[] (para categorias)
 *   baseAdmin?: {[field]: value}
 *   testIdPrefix
 */
export default function AdminSeasonMatrix({
  seasons = [],
  categories = [],
  zoneKey,
  seasonPrices = {},
  onChange,
  basePrices = {},
  priceFields = null,
  baseAdmin = {},
  testIdPrefix = "adm-mtx",
}) {
  const isFieldsMode = Array.isArray(priceFields) && priceFields.length > 0;
  const columns = isFieldsMode ? priceFields : categories.map((c) => c.key);
  const columnLabels = isFieldsMode
    ? priceFields.map((k) => ({
        key: k,
        label: k === "base_price_eur" ? "Base €" : k === "price_per_km" ? "€/km" : k === "price_per_minute" ? "€/min" : k,
      }))
    : categories.map((c) => ({ key: c.key, label: c.label }));

  const applicableSeasons = useMemo(() => {
    return seasons.filter((s) => s.scope === "global" || (s.scope === "zone" && s.zone_key === zoneKey));
  }, [seasons, zoneKey]);

  const baseFor = (col) => isFieldsMode ? baseAdmin[col] : basePrices[col];

  const setPrice = (seasonId, col, value) => {
    const sp = { ...(seasonPrices || {}) };
    const entry = { ...(sp[seasonId] || {}) };
    if (value === "" || value === null || value === undefined) {
      delete entry[col];
    } else {
      entry[col] = Number(value);
    }
    if (Object.keys(entry).length === 0) delete sp[seasonId];
    else sp[seasonId] = entry;
    onChange(sp);
  };

  if (applicableSeasons.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500">
        {zoneKey ? `Sem temporadas aplicáveis à zona "${zoneKey}".` : "Sem temporadas activas."} Crie temporadas em <a href="/admin/seasons" className="text-[#f59e0b] underline">/admin/seasons</a>.
      </div>
    );
  }

  return (
    <div className="border border-slate-200 bg-white">
      <div className="p-3 border-b border-slate-200 bg-slate-50/50 flex items-center gap-2 text-[11px] text-slate-600">
        <Info className="w-3.5 h-3.5 text-[#f59e0b]" />
        <span>Defina preço específico por temporada. <strong>Vazio</strong> = usa preço base deste serviço.</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="p-2 text-left text-[10px] uppercase tracking-widest font-bold text-slate-500 w-52">Temporada</th>
              {columnLabels.map((c) => (
                <th key={c.key} className="p-2 text-right text-[10px] uppercase tracking-widest font-bold text-slate-500 min-w-[80px]">
                  {c.label}
                  {baseFor(c.key) != null && baseFor(c.key) > 0 && (
                    <div className="text-[9px] text-slate-400 font-mono normal-case">base €{Number(baseFor(c.key)).toFixed(0)}</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {applicableSeasons.map((s) => (
              <tr key={s.id} className="border-b border-slate-100" data-testid={`${testIdPrefix}-row-${s.id}`}>
                <td className="p-2 align-top">
                  <div className="text-sm font-semibold text-[#0a0f1e] flex items-center gap-1.5">
                    <CalendarRange className="w-3.5 h-3.5 text-[#f59e0b]" /> {s.name}
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1">
                    {s.scope === "global" ? <Globe className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
                    <span>{s.scope === "global" ? "Global" : `${s.zone_key}`}</span>
                    <span>· prio {s.priority}</span>
                  </div>
                </td>
                {columns.map((col) => {
                  const val = (seasonPrices[s.id] || {})[col] ?? "";
                  return (
                    <td key={col} className="p-2">
                      <Input
                        type="number" min="0" step="0.5"
                        value={val}
                        placeholder="—"
                        onChange={(e) => setPrice(s.id, col, e.target.value)}
                        className="w-24 h-8 text-right !text-sm ml-auto"
                        data-testid={`${testIdPrefix}-${s.id}-${col}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
